/**
 * Full-flow hydrate reconcile for preferences questionnaire session.
 *
 * Belongs here: wipe-once / seed / ignore-after-done → ApplyHydration.
 * Does not belong here: answering, leave, PreparePreferenceTypeEdit, or navigation.
 */
import { useLayoutEffect, useRef, type Dispatch } from 'react'
import type { PreferenceQuestionnaireDataResponse } from '../setup/types'
import type { QuestionnaireStatus } from '../../shared/types/questionnaire.types'
import {
  getFullFlowHydrationPlan,
  isSameAppliedHydrationStatus,
  INITIAL_FULL_FLOW_HYDRATION_PROGRESS,
  type FullFlowHydrationProgress
} from '../../shared/sessionHydrationPolicy'
import { buildInitialAnswersFromPreferencesPayload } from '../../question-utils/preferencesInitialAnswers'
import type { PreferencesAnswersMap } from '../setup/types'
import type { ExpertReviewStatus } from '../../providers/ExpertReviewStatusContext'
import { PreferencesSessionActionType, type PreferencesSessionAction } from './preferencesSessionReducer'

export type UsePreferencesSessionHydrationArgs = {
  preferenceTypeIdFromPathParam: string | undefined
  rawQuestionnaireData: PreferenceQuestionnaireDataResponse | undefined
  committedAnswers: PreferencesAnswersMap
  expertReviewStatus: ExpertReviewStatus
  dispatch: Dispatch<PreferencesSessionAction>
}

/**
 * Applies FullFlow hydration from /status.
 * Single-type edit routes are skipped — PreparePreferenceTypeEdit owns those answers.
 */
export function usePreferencesSessionHydration({
  preferenceTypeIdFromPathParam,
  rawQuestionnaireData,
  committedAnswers,
  expertReviewStatus,
  dispatch
}: UsePreferencesSessionHydrationArgs) {
  const { isStatusLoading, isStatusError, questionnaireStatus } = expertReviewStatus
  // Last status we already applied for wipe/seed — skip repeats while answering
  const lastAppliedStatusRef = useRef<QuestionnaireStatus | null | undefined>(undefined)
  const hydrationProgressRef = useRef<FullFlowHydrationProgress>(INITIAL_FULL_FLOW_HYDRATION_PROGRESS)
  const hasLocalCommittedAnswers = Object.keys(committedAnswers).length > 0

  useLayoutEffect(() => {
    if (preferenceTypeIdFromPathParam?.trim()) return
    if (isStatusLoading) return
    if (isStatusError) return

    const plan = getFullFlowHydrationPlan(
      questionnaireStatus,
      hydrationProgressRef.current,
      hasLocalCommittedAnswers
    )
    hydrationProgressRef.current = plan.hydrationProgress

    if (plan.action === 'skip') return

    if (plan.action === 'wipe') {
      dispatch({
        type: PreferencesSessionActionType.ApplyHydration,
        draftAnswers: {},
        committedAnswers: {},
        resetCursorToStart: plan.resetCursorToStart,
        hasReachedReviewOnce: false,
        answersVersionPolicy: 'wipe'
      })
      lastAppliedStatusRef.current = questionnaireStatus
      return
    }

    // seed — same applied done-status already applied → avoid clobbering
    if (isSameAppliedHydrationStatus(lastAppliedStatusRef.current, questionnaireStatus)) return
    if (!rawQuestionnaireData) return

    const answers = buildInitialAnswersFromPreferencesPayload(rawQuestionnaireData)
    // Payload can lag POST success — don't replace good local answers with an empty seed
    if (Object.keys(answers)?.length === 0) return

    dispatch({
      type: PreferencesSessionActionType.ApplyHydration,
      draftAnswers: answers,
      committedAnswers: answers,
      resetCursorToStart: plan.resetCursorToStart,
      hasReachedReviewOnce: true,
      answersVersionPolicy: 'seed'
    })
    lastAppliedStatusRef.current = questionnaireStatus
  }, [
    rawQuestionnaireData,
    hasLocalCommittedAnswers,
    preferenceTypeIdFromPathParam,
    isStatusLoading,
    isStatusError,
    questionnaireStatus,
    dispatch
  ])
}
